<template>
  <v-dialog v-model="open" max-width="560" @update:model-value="onToggle">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="me-2">mdi-information-outline</v-icon>
        About WayWyser
        <v-spacer />
        <v-btn icon variant="text" density="comfortable" @click="ui.closeAbout()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-alert v-if="error" type="error" density="compact" class="mb-3">{{ error }}</v-alert>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

        <!-- ── CLIENT (this browser) ──────────────────────────────────── -->
        <div class="text-subtitle-2 font-weight-bold mb-1">Client (this browser)</div>
        <v-table density="compact" class="bg-transparent">
          <tbody>
            <tr>
              <td><v-icon size="small" color="primary" class="me-2">mdi-tag</v-icon>App version</td>
              <td class="text-right"><strong>{{ runningBuild }}</strong></td>
              <td class="text-right" style="width: 36px">
                <v-icon v-if="appMatch === true" color="success" size="small">mdi-check-circle</v-icon>
                <v-icon v-else-if="appMatch === false" color="warning" size="small">mdi-alert-circle</v-icon>
              </td>
            </tr>
          </tbody>
        </v-table>

        <div class="mt-3 d-flex align-center" style="gap: 12px">
          <v-chip :color="status.color" size="small" label>{{ status.label }}</v-chip>
          <span v-if="version?.releaseDate" class="text-caption text-grey">
            Latest release {{ formatDate(version.releaseDate) }}
          </span>
        </div>
        <p v-if="version?.description" class="text-caption text-grey mt-2">
          {{ version.description }}
        </p>
        <!-- Server DB is not a 2.x number — show its migration state, not a count. -->
        <div v-if="version?.serverDbUpToDate != null" class="mt-1 d-flex align-center" style="gap: 8px">
          <v-icon v-if="version.serverDbUpToDate" color="success" size="small">mdi-database-check</v-icon>
          <v-icon v-else color="warning" size="small">mdi-database-alert</v-icon>
          <span class="text-caption text-grey">
            <template v-if="version.serverDbUpToDate">Server DB up to date</template>
            <template v-else>Server DB behind — {{ version.pendingMigrationsCount }} migration(s) pending (run Update-Database)</template>
            <span class="text-disabled"> · {{ version.appliedMigrationsCount ?? "—" }} applied</span>
          </span>
        </div>

        <!-- ── MOBILE (published target) ──────────────────────────────── -->
        <v-divider class="my-4" />
        <div class="text-subtitle-2 font-weight-bold mb-1">Mobile app</div>
        <p class="text-caption text-grey mb-0">
          Phones should be running <strong>{{ version?.versionNr || "—" }}</strong>. The mobile
          app shows its own installed version and DB state in its own About screen.
        </p>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" variant="text" @click="ui.closeAbout()">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
/* global __APP_VERSION__ */
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useUIStore } from "@/stores/ui";
import { getLatestVersion } from "@/api/softwareVersion";

const ui = useUIStore();
const { aboutOpen: open } = storeToRefs(ui);

// The TRUE running version is compiled into the build, not read from the DB.
const runningBuild = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "—";

const version = ref(null);
const loading = ref(false);
const error = ref(null);
let loadedOnce = false;

// dd/mm/yyyy
function formatDate(d) {
  return d ? new Date(d).toLocaleDateString("en-GB") : "—";
}

// Numeric-dotted comparison: returns -1/0/1, or null if not comparable.
function compareVersions(a, b) {
  if (!a || !b) return null;
  const pa = String(a).split(".").map((n) => parseInt(n, 10));
  const pb = String(b).split(".").map((n) => parseInt(n, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

const appMatch = computed(() => {
  const cmp = compareVersions(runningBuild, version.value?.versionNr);
  if (cmp == null) return null;
  return cmp === 0;
});

const status = computed(() => {
  if (!version.value) return { label: "Unknown", color: "grey" };
  const cmp = compareVersions(runningBuild, version.value.versionNr);
  if (cmp == null) return { label: "Published version unknown", color: "grey" };
  if (cmp < 0) return { label: "Update available — refresh your browser", color: "warning" };
  if (cmp > 0) return { label: "Ahead of published version", color: "info" };
  return { label: "Up to date", color: "success" };
});

async function load() {
  loading.value = true;
  error.value = null;
  try {
    version.value = await getLatestVersion();
    loadedOnce = true;
  } catch (e) {
    console.error(e);
    error.value =
      e.response?.data?.detail || e.response?.data?.message || e.message || "Failed to load version";
  } finally {
    loading.value = false;
  }
}

function onToggle(v) {
  if (!v) ui.closeAbout();
}

// Load the first time the dialog is opened.
watch(open, (isOpen) => {
  if (isOpen && !loadedOnce) load();
});
</script>
